import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Video,
  Sparkles,
  Scissors,
  Layers,
  Link2,
  FileArchive,
  Mic,
  RotateCw,
  Play,
  CheckCircle,
  Crown,
  X,
  AudioLines,
  Image,
  Type,
  Captions,
  ChevronLeft,
  ChevronRight,
  Radio,
  Store,
  Briefcase,
  MessageSquare,
  Users,
  Globe,
} from 'lucide-react';
import { Footer } from './Footer';
import SiteScan from '@/components/SiteScan';
import { useSubscription } from '@/hooks/useSubscription';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { useToast } from '@/hooks/use-toast';
import { ReaderAvatar, BrandCharacter, Splotch } from '@/components/brand';
import { HomeDemoSection } from './HomeDemoSection';
import { fetchHomepageHero, type CreatorProduct, type PublicProductCreator, type HeroSlideDef } from '@/services/creatorApi';
import { openCommunity } from '@/config/community';

// Hero slides and the "Classes & featured events" grid both come from
// /api/homepage/hero, which admins edit at /admin (docs/PLAN_homepage_hero_admin.md).
// Nothing about a product is hardcoded here anymore.

// Gold ink used for the eyebrow/tag accents that sit on beige cards (matches the
// design mockup's --gold-ink).
const GOLD_INK = '#9c6f00';

// ── Tool catalog — mirrors home.jsx TOOLS array. `hot` toggles the pink-
//    accent featured treatment; `usesCredits` shows the credits dot on the card.
const TOOLS: {
  id: string;
  name: string;
  blurb: string;
  Icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  route: string;
  tag?: string;
  hot?: boolean;
  usesCredits?: boolean;
}[] = [
  { id: 'audition',          name: 'Audition Enhancer', tag: 'Featured', blurb: 'Teleprompter, scene partner, color grade, audio polish — one pipeline.', Icon: Video,      route: '/audition',          hot: true, usesCredits: true },
  { id: 'video-enhancer',    name: 'Video Enhancer',    tag: 'AI · Video tokens', blurb: 'Browser-based NLE. Manual timeline or AI script-to-edit pre-cuts.',     Icon: Sparkles,   route: '/video-enhancer',    hot: true, usesCredits: true },
  { id: 'audio-enhancer',    name: 'Audio Enhancer',    tag: 'New', blurb: 'De-noise, de-reverb, level — studio-grade voice cleanup.',                      Icon: AudioLines, route: '/audio-enhancer',    hot: true, usesCredits: true },
  { id: 'background-remover', name: 'Background Remover', tag: 'New',  blurb: 'Drop the room — green-screen, stock scenes, or AI backdrops.',               Icon: Image,      route: '/background-remover', usesCredits: true },
  { id: 'name-slate',        name: 'Name Slate',        tag: 'New',    blurb: 'Name, role, agency — first frame or lower third.',                           Icon: Type,       route: '/name-slate',        usesCredits: true },
  { id: 'captions',          name: 'Captions',          tag: 'New',    blurb: 'Auto-transcribe and burn styled captions into your tape.',                   Icon: Captions,   route: '/captions',          usesCredits: true },
  { id: 'video-splitter',    name: 'Splitter / Trimmer',                blurb: 'Split clips into segments or trim to an exact duration.',                  Icon: Scissors,   route: '/video-splitter' },
  { id: 'video-stitcher',    name: 'Stitcher',                          blurb: 'Two clips, side-by-side or stacked — one frame.',                          Icon: Layers,     route: '/video-stitcher' },
  { id: 'video-concatenate', name: 'Merge',                             blurb: 'Join multiple clips end-to-end into one video.',                           Icon: Link2,      route: '/video-concatenate' },
  { id: 'video-compressor',  name: 'Compressor',                        blurb: 'Shrink file size, keep the quality.',                                      Icon: FileArchive, route: '/video-compressor' },
  { id: 'video-transcriber', name: 'Transcribe',                        blurb: 'Pull speech out of any clip. Captions, scripts, search.',                  Icon: Mic,        route: '/video-transcriber' },
  { id: 'video-rotator',     name: 'Rotator',                           blurb: 'Fix sideways shots. 90°, 180°, 270°.',                                      Icon: RotateCw,   route: '/video-rotator' },
];

// ── The four pillars — one membership, four ways to move a career forward.
//    Pillar 3 ("Your Own Store") is framed around building a storefront and
//    selling your own products; webinars/classes are examples of what you sell.
type Pillar = {
  n: number;
  tag: string;
  short: string;
  title: string;
  blurb: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  to?: string;
  href?: string;
  cta: string;
  live?: boolean;
  accent: string;
};
const PILLARS: Pillar[] = [
  {
    n: 1,
    tag: 'Perform your best',
    short: 'Enhancer Tools',
    title: 'Video & Audition Enhancer Tools',
    blurb: 'Turn a rough take into a polished, camera-ready audition — no studio required.',
    Icon: Video,
    to: '/audition',
    cta: 'Open the studio',
    accent: GOLD_INK,
  },
  {
    n: 2,
    tag: 'Never read alone',
    short: 'Book a Reader',
    title: 'Book a Reader',
    blurb: 'Get a real scene partner on demand to rehearse and record stronger takes.',
    Icon: MessageSquare,
    to: '/readers',
    cta: 'Browse readers',
    accent: GOLD_INK,
  },
  {
    n: 3,
    tag: 'Build & sell',
    short: 'Your Own Store',
    title: 'Your Own Store',
    blurb: 'Open your own storefront and sell what you know — featured events, classes, courses, and video series — to an audience you own.',
    Icon: Store,
    to: '/my-store',
    cta: 'Start your store',
    accent: GOLD_INK,
  },
  {
    n: 4,
    tag: 'Get hired',
    short: 'Jobs Marketplace',
    title: 'Jobs Marketplace',
    blurb: 'Browse and apply to real paid gigs — UGC, student films, corporate & marketing videos, indie films and more.',
    Icon: Briefcase,
    // Casting calls live on the talent profile (casting.heyjoe.io/casting-calls
    // opens the profile's creator board tab). Never link out to castingstation —
    // it is not our product and it is not where these jobs are.
    href: 'https://casting.heyjoe.io/casting-calls',
    cta: 'Browse gigs',
    live: true,
    accent: 'var(--hj-green)',
  },
];

// ── Hero — rotating carousel ("What's on Hey Joe Plus") ─────────────
// Six slides: the two featured live events first — Ronen Rubinstein's conversation,
// John Wroblewski's coach talk — then the brand hero ("Everything you need to book
// more work"), Audition Enhancer, Creator store, and the "Get Seen" featured event.
// The three event slides fetch live so they're never stale/made-up. Autoplays every 5.5s, pauses on hover. The track
// transform is set imperatively in a useEffect — binding it to a value that
// re-renders would restart the CSS transition every frame and it'd never commit.
export const MARKER: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  padding: '0.04em 0.2em',
  background: 'var(--hj-yellow)',
  color: 'oklch(0.18 0.005 70)',
  clipPath: 'polygon(2% 8%, 98% 0%, 100% 92%, 0% 100%)',
  transform: 'rotate(-1.5deg)',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
};

const heroNavBtn: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 999,
  background: 'transparent',
  border: '1.5px solid var(--hj-hairline)',
  color: 'var(--hj-ink)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export interface HeroCTA { label: string; to?: string; href?: string; primary?: boolean; icon?: React.ReactNode; }

export const HeroSlide: React.FC<{
  kicker: string;
  kickerColor: string;
  headline: React.ReactNode;
  subhead: string;
  ctas: HeroCTA[];
  tile: React.ReactNode;
}> = ({ kicker, kickerColor, headline, subhead, ctas, tile }) => (
  <div className="hj-hero-grid grid items-stretch gap-6 md:gap-10" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
    <div className="flex flex-col justify-between gap-6 min-w-0">
      <div>
        <div className="mono-label" style={{ color: kickerColor, marginBottom: 14 }}>{kicker}</div>
        <h1
          className="font-hj-display m-0"
          style={{ fontSize: 'clamp(40px, 5.4vw, 68px)', lineHeight: 0.95, color: 'var(--hj-ink)', fontWeight: 800, letterSpacing: '-0.035em' }}
        >
          {headline}
        </h1>
        <p style={{ maxWidth: 520, margin: '18px 0 0', color: 'var(--hj-ink-2)', fontSize: 15.5, lineHeight: 1.5 }}>{subhead}</p>
      </div>
      <div className="flex items-center gap-2.5 flex-wrap">
        {ctas.map((c, i) => {
          const style: React.CSSProperties = c.primary
            ? { padding: '12px 20px', borderRadius: 999, background: 'var(--hj-yellow)', border: '1px solid var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', textDecoration: 'none', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }
            : { padding: '12px 18px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit', textDecoration: 'none', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 };
          const inner = <>{c.icon}{c.label}{c.primary && <ArrowRight size={14} strokeWidth={2.4} />}</>;
          return c.href
            ? <a key={i} href={c.href} target="_blank" rel="noopener noreferrer" style={style}>{inner}</a>
            : <Link key={i} to={c.to || '#'} style={style}>{inner}</Link>;
        })}
      </div>
    </div>
    {tile}
  </div>
);

const heroTileBase: React.CSSProperties = {
  borderRadius: 'var(--hj-r-lg)',
  background: 'var(--hj-beige)',
  border: '1px solid var(--hj-beige-line)',
  minHeight: 420,
};

// Slide 1 tile — a compact 2×2 preview of the four pillars. Each cell leads with
// an accent-tinted icon and anchors its label to the bottom, so "four pillars,
// one membership" reads at a glance with real energy. No filler sequence numbers
// — the pillars are parallel, not steps.
const PILLAR_ACCENT: Record<number, string> = { 1: '#c1478f', 2: '#3f6cf0', 3: '#b9791e', 4: 'var(--hj-green)' };
const PILLAR_MINI: Record<number, string> = {
  1: 'Polish any take to camera-ready.',
  2: 'A real scene partner, on demand.',
  3: 'Sell classes & courses you own.',
  4: 'Apply to real paid gigs.',
};
const HeroPillarsTile: React.FC = () => (
  <div className="relative overflow-hidden flex flex-col" style={{ ...heroTileBase, padding: 20 }}>
    <div className="flex items-center gap-2" style={{ marginBottom: 14 }}>
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--hj-green)' }} />
      <span className="mono-label" style={{ color: 'var(--hj-beige-ink-2)' }}>Four pillars · one membership</span>
    </div>
    <div className="grid flex-1" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {PILLARS.map((p) => {
        const accent = PILLAR_ACCENT[p.n] || 'var(--hj-beige-ink)';
        return (
          <div
            key={p.n}
            className="flex flex-col"
            style={{ background: '#fff', border: `1px solid ${p.live ? 'color-mix(in oklch, var(--hj-green) 40%, var(--hj-beige-line))' : 'var(--hj-beige-line)'}`, borderRadius: 'var(--hj-r-md)', padding: '15px 15px 16px' }}
          >
            <div className="flex items-start justify-between">
              <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 11, background: `color-mix(in oklch, ${accent} 15%, #fff)`, color: accent }}>
                <p.Icon size={19} strokeWidth={2} />
              </span>
              {p.live && (
                <span className="mono-label inline-flex items-center gap-1" style={{ padding: '3px 9px', borderRadius: 999, background: 'var(--hj-green)', color: '#fff', fontSize: 8.5 }}>
                  <span aria-hidden style={{ width: 4, height: 4, borderRadius: '50%', background: '#fff' }} />Live
                </span>
              )}
            </div>
            <div style={{ flex: 1, minHeight: 12 }} />
            <div className="font-hj-display" style={{ fontSize: 15, fontWeight: 800, color: 'var(--hj-beige-ink)', lineHeight: 1.12, letterSpacing: '-0.01em' }}>{p.short}</div>
            <div className="mono-label" style={{ marginTop: 5, color: accent }}>{p.tag}</div>
            <p style={{ margin: '7px 0 0', fontSize: 11.5, lineHeight: 1.4, color: 'var(--hj-beige-ink-2)' }}>{PILLAR_MINI[p.n]}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const StatFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>{label}</span>
    <span className="truncate" style={{ fontSize: 15, color: 'var(--hj-beige-ink)', fontWeight: 700 }}>{value}</span>
  </div>
);

// Slide 2 tile — the Joe-mode phone doodle over a splotch, with the status band.
const AuditionTile: React.FC = () => (
  <div className="relative overflow-hidden flex flex-col" style={heroTileBase}>
    <div className="absolute flex items-center" style={{ top: 18, left: 18, zIndex: 3 }}>
      <div className="flex items-center gap-2" style={{ padding: '6px 10px', borderRadius: 999, background: 'oklch(0.16 0.005 70)', color: 'oklch(0.96 0.012 88)' }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--hj-rec)', boxShadow: '0 0 8px var(--hj-rec)', animation: 'hj-rec-blink 1.4s ease-in-out infinite' }} />
        <span className="mono-label" style={{ color: 'inherit', fontSize: 10 }}>Studio · ready</span>
      </div>
    </div>
    <div aria-hidden style={{ position: 'absolute', top: 22, right: 22, zIndex: 3, transform: 'rotate(-4deg)', fontFamily: 'var(--hj-font-script)', fontWeight: 700, fontSize: 26, color: 'oklch(0.18 0.005 70)', lineHeight: 0.95, textAlign: 'right' }}>
      right from<br />your phone.
    </div>
    <div className="flex-1 relative flex items-center justify-center" style={{ padding: '40px 24px 16px', minHeight: 280 }}>
      <Splotch variant={2} size={420} opacity={0.85} rotate={-8} style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -48%) rotate(-8deg)', maxWidth: '88%' }} />
      <BrandCharacter which="phone" size={240} tone="original" style={{ position: 'relative', zIndex: 2 }} />
    </div>
    <div className="grid" style={{ padding: '16px 20px', borderTop: '1px solid var(--hj-beige-line)', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink)' }}>
      <StatLight label="Shoot" value="Anywhere" />
      <StatLight label="Polish" value="One-tap clean" />
      <StatLight label="Send" value="Direct link" />
    </div>
  </div>
);

const CREATOR_ROWS = [
  { title: 'Featured events & live classes', persona: 'Sell seats to your audience', icon: Radio, tint: 'var(--hj-pink-tint)', glyph: '#b3438f' },
  { title: 'On-demand courses', persona: 'Package what you teach', icon: Layers, tint: 'var(--hj-yellow-tint)', glyph: '#9a7d05' },
  { title: 'Videos, shows & series', persona: 'Build a following you own', icon: Video, tint: 'rgb(65 113 249 / 0.12)', glyph: '#3a5fd0' },
];

// Slide 3 tile — your store: the products you can sell + an ownership band.
const CreatorTile: React.FC = () => (
  <div className="relative overflow-hidden flex flex-col" style={heroTileBase}>
    <div aria-hidden style={{ position: 'absolute', top: 20, right: 20, zIndex: 3, transform: 'rotate(-4deg)', fontFamily: 'var(--hj-font-script)', fontWeight: 700, fontSize: 23, color: 'oklch(0.18 0.005 70)', lineHeight: 0.95, textAlign: 'right' }}>
      your store,<br />your rules.
    </div>
    <div className="flex-1 flex flex-col justify-center gap-2.5" style={{ padding: '44px 20px 16px' }}>
      {CREATOR_ROWS.map((r) => {
        const Icon = r.icon;
        return (
          <div key={r.title} className="flex items-center gap-3" style={{ padding: '12px 14px', borderRadius: 'var(--hj-r-md)', background: '#fff', border: '1px solid var(--hj-beige-line)' }}>
            <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 11, background: r.tint, color: r.glyph }}>
              <Icon className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-hj-display" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}>{r.title}</div>
              <div className="mono-label" style={{ marginTop: 2, color: r.glyph }}>{r.persona}</div>
            </div>
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-beige-ink-3)' }} />
          </div>
        );
      })}
    </div>
    <div className="text-center" style={{ padding: '16px 20px', borderTop: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-deep)' }}>
      <span className="font-hj-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}>Teach it. Sell it. Own it.</span>
    </div>
  </div>
);

// Hey Joe Sites tile — a browser window with a plain actor site in it, so the
// slide shows the thing itself: an address under the actor's own name.
const SitesTile: React.FC = () => (
  <div className="relative overflow-hidden flex flex-col" style={heroTileBase}>
    <div aria-hidden style={{ position: 'absolute', top: 20, right: 20, zIndex: 3, transform: 'rotate(-4deg)', fontFamily: 'var(--hj-font-script)', fontWeight: 700, fontSize: 23, color: 'oklch(0.18 0.005 70)', lineHeight: 0.95, textAlign: 'right' }}>
      your site,<br />your name.
    </div>
    {/* The brand laptop doodle on a splotch (Joe, Oct 25 2026), same framing as the audition tile. */}
    <div className="flex-1 relative flex items-center justify-center" style={{ padding: '40px 24px 16px', minHeight: 280 }}>
      <Splotch variant={1} size={440} opacity={0.85} rotate={6} style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -46%) rotate(6deg)', maxWidth: '90%' }} />
      <BrandCharacter which="laptop-duo" size={210} tone="original" style={{ position: 'relative', zIndex: 2, maxWidth: '88%', height: 'auto', width: 300 }} />
    </div>
    <div className="grid" style={{ padding: '16px 20px', borderTop: '1px solid var(--hj-beige-line)', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink)' }}>
      <StatLight label="Upload" value="A zip of files" />
      <StatLight label="Publish" value="One button" />
      <StatLight label="Mail" value="Form to inbox" />
    </div>
  </div>
);

// Slides 4–6 tile — the real featured product (data fetched live).
const resolveImg =(v?: string) => (v ? (v.startsWith('http') || v.startsWith('/api/') ? v : `/api/s3/${v}`) : '');
export const WebinarTile: React.FC<{ product: CreatorProduct | null; creator: PublicProductCreator | null; to?: string; full?: boolean }> = ({ product, creator, to, full }) => {
  const cover = resolveImg(product?.coverImage) || '/api/s3/creator-products/joe-katia-banner.png';
  const dateStr = product?.eventDate ? new Date(product.eventDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : null;
  const free = !product || product.price === 0;
  // Full-frame variant: the cover takes the whole right half of the hero, edge to
  // edge, with the title bar along the bottom. For artwork built to fill the tile
  // (partner lockups, posters) instead of a card floating in cream. The image box
  // keeps the artwork's 16:9 shape at every width: stretched to the hero's height,
  // object-fit cover cropped the sides off text-heavy covers (the AMA slide).
  if (full) {
    const body = (
      <div className="relative overflow-hidden flex flex-col" style={{ ...heroTileBase, minHeight: 0, padding: 0, background: '#fff' }}>
        <div className="relative" style={{ aspectRatio: '16 / 9', background: 'var(--hj-beige)' }}>
          <img src={cover} alt="" className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span className="mono-label absolute" style={{ top: 16, left: 16, padding: '5px 12px', borderRadius: 999, background: 'oklch(0.16 0.005 70 / 0.88)', color: '#fff' }}>{product?.eventDate ? `Live featured event · ${dateStr}` : 'On-demand course'}</span>
        </div>
        <div className="flex items-center justify-between gap-4" style={{ padding: '14px 18px', borderTop: '1px solid var(--hj-beige-line)', background: '#fff' }}>
          <div className="min-w-0">
            <div className="font-hj-display truncate" style={{ fontSize: 17, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em' }}>{product?.title || 'Get Seen on Hey Joe'}</div>
            <div className="truncate" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', marginTop: 2 }}>{creator?.displayName || 'Hey Joe'}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(product as any)?.access?.restrictedTo || (product as any)?.restrictToAgencies?.length
              ? <span className="mono-label" style={{ padding: '6px 10px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)' }}>Exclusive</span>
              : <span className="tabular" style={{ fontSize: 16, fontWeight: 800, color: 'var(--hj-beige-ink)', fontFamily: 'var(--hj-font-mono)' }}>{free ? 'Free' : `$${((product!.price) / 100).toFixed(0)}`}</span>}
            <span className="inline-flex items-center gap-1.5" style={{ padding: '8px 16px', borderRadius: 999, background: 'oklch(0.16 0.005 70)', color: '#fff', fontSize: 13, fontWeight: 700 }}>
              {product?.eventDate ? 'Register' : 'Get the course'} <ArrowRight size={13} strokeWidth={2.4} />
            </span>
          </div>
        </div>
      </div>
    );
    return to
      ? <Link to={to} aria-label={`View ${product?.title || 'event'}`} className="block self-center" style={{ textDecoration: 'none' }}>{body}</Link>
      : <div className="self-center">{body}</div>;
  }
  const card = (
    <div className="w-full" style={{ maxWidth: 360, background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 18px 50px oklch(0.1 0.02 60 / 0.28)', cursor: to ? 'pointer' : 'default' }}>
      <div className="relative" style={{ aspectRatio: '16 / 9', background: 'var(--hj-beige-deep)' }}>
        <img src={cover} alt="" className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <span className="mono-label absolute" style={{ top: 12, left: 12, padding: '4px 10px', borderRadius: 999, background: 'oklch(0.16 0.005 70 / 0.82)', color: '#fff' }}>{product?.eventDate ? `Live featured event · ${dateStr}` : 'On-demand course'}</span>
      </div>
      <div style={{ padding: '16px 18px' }}>
        <div className="font-hj-display" style={{ fontSize: 18, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em', lineHeight: 1.2 }}>{product?.title || 'Get Seen on Hey Joe'}</div>
        <div className="flex items-center gap-2.5" style={{ marginTop: 12 }}>
          <span className="overflow-hidden flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, background: 'oklch(0.18 0.005 70)' }}>
            {creator?.imageUrl ? <img src={resolveImg(creator.imageUrl)} alt="" className="w-full h-full" style={{ objectFit: 'cover' }} /> : null}
          </span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>{creator?.displayName || 'Joe B'}</div>
            <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>Hey Joe</div>
          </div>
        </div>
        <div className="flex items-center justify-between" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--hj-beige-line)' }}>
          <span className="flex flex-col" style={{ lineHeight: 1.1 }}>
            <span className="tabular" style={{ fontSize: 20, fontWeight: 800, color: 'var(--hj-beige-ink)', fontFamily: 'var(--hj-font-mono)' }}>{free ? 'Free' : `$${((product!.price) / 100).toFixed(0)}`}</span>
            {!free && typeof product?.memberPriceCents === 'number' && product.memberPriceCents < product.price && (
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--hj-beige-ink)', marginTop: 3 }}>{product.memberPriceCents === 0 ? 'Free for Plus members' : `$${(product.memberPriceCents / 100).toFixed(0)} for Plus members`}</span>
            )}
          </span>
          <span className="inline-flex items-center gap-1.5" style={{ padding: '8px 16px', borderRadius: 999, background: 'oklch(0.16 0.005 70)', color: '#fff', fontSize: 13, fontWeight: 700 }}>
            {product?.eventDate ? 'Reserve' : 'Get the course'} <ArrowRight size={13} strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </div>
  );
  return (
    <div className="relative overflow-hidden flex items-center justify-center" style={{ ...heroTileBase, padding: 24 }}>
      {to
        ? <Link to={to} aria-label={`View ${product?.title || 'event'}`} className="w-full flex justify-center" style={{ textDecoration: 'none' }}>{card}</Link>
        : card}
    </div>
  );
};


const HomeHero: React.FC = () => {
  // Opens on the lead featured event (Ronen, slide 1), then John's coach talk; the
  // brand hero and product stories follow as the carousel advances.
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  // Pause-on-hover only on hover-capable devices — on touch, mouseenter fires on
  // tap with no matching mouseleave, which would freeze autoplay.
  const canHover = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

  // Slide definitions from the server; the static three render if it can't be reached.
  const [defs, setDefs] = useState<HeroSlideDef[]>([
    { id: 'brand', enabled: true, kind: 'static', staticKey: 'brand' },
    { id: 'audition', enabled: true, kind: 'static', staticKey: 'audition' },
    { id: 'creator', enabled: true, kind: 'static', staticKey: 'creator' },
  ]);
  useEffect(() => {
    fetchHomepageHero().then((r) => { if (r.slides?.length) { setDefs(r.slides); setIndex(0); } }).catch(() => {});
  }, []);
  const SLIDE_COUNT = defs.length;

  useEffect(() => {
    if (trackRef.current) trackRef.current.style.transform = `translateX(-${index * 100}%)`;
  }, [index]);

  useEffect(() => {
    if (paused || SLIDE_COUNT < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDE_COUNT), 5500);
    return () => clearInterval(t);
  }, [paused, SLIDE_COUNT]);

  const go = (n: number) => setIndex(((n % SLIDE_COUNT) + SLIDE_COUNT) % SLIDE_COUNT);

  const KICKER: Record<string, string> = { yellow: 'var(--hj-yellow-ink)', pink: 'var(--hj-pink-ink)', ink: 'var(--hj-ink-3)' };
  const staticSlides: Record<string, React.ReactElement> = {
    brand: (
      <HeroSlide
        key="brand"
        kicker="The membership built for working talent"
        kickerColor="var(--hj-ink-3)"
        headline={<><span style={{ display: 'block' }}>Everything you need to{' '}</span><span style={MARKER}>book more work.</span></>}
        subhead="Sharpen your self-tape, book a reader, build your own store, and land paid gigs — all from one membership, built by a casting veteran."
        ctas={[
          { label: 'Join Plus — free', to: '/plans', primary: true },
          { label: 'Open Store', to: '/marketplace', icon: <Store size={14} strokeWidth={2} /> },
          { label: 'Casting Calls', href: 'https://casting.heyjoe.io/casting-calls', icon: <Briefcase size={14} strokeWidth={2} /> },
        ]}
        tile={<HeroPillarsTile />}
      />
    ),
    audition: (
      <HeroSlide
        key="audition"
        kicker="Featured tool"
        kickerColor="var(--hj-pink-ink)"
        headline={<><span style={{ display: 'block' }}>All you need for your{' '}</span><span style={MARKER}>audition tape.</span></>}
        subhead="Teleprompter, scene partner, color grade, and audio polish — one pipeline, right from your phone."
        ctas={[{ label: 'Open Audition Enhancer', to: '/audition', primary: true }, { label: '60s demo', href: 'https://www.instagram.com/reel/DVRnPX9kqFQ/', icon: <Play size={13} strokeWidth={2} /> }]}
        tile={<AuditionTile />}
      />
    ),
    creator: (
      <HeroSlide
        key="creator"
        kicker="New · Your own store"
        kickerColor="var(--hj-yellow-ink)"
        headline={<><span style={{ display: 'block' }}>Turn what you know{' '}</span><span style={MARKER}>into income.</span></>}
        subhead="Open your own store on Hey Joe — sell featured events, classes, courses, and video series, and own the audience you build."
        ctas={[{ label: 'Start your store — free', to: '/my-store', primary: true }, { label: 'See creator tools', to: '/my-store' }]}
        tile={<CreatorTile />}
      />
    ),
    sites: (
      <HeroSlide
        key="sites"
        kicker="New · Hey Joe Sites"
        kickerColor="var(--hj-yellow-ink)"
        headline={<><span style={{ display: 'block' }}>Your acting site,{' '}</span><span style={MARKER}>under your own name.</span></>}
        subhead="Host your website on Hey Joe Plus. Upload your files, check a private preview, publish with one button. A built-in contact form sends messages to your inbox, and your own domain connects with one record."
        ctas={[{ label: 'Open My Site', to: '/my-site', primary: true, icon: <Globe size={14} strokeWidth={2} /> }, { label: 'Moving from another site builder?', to: '/move-your-site' }]}
        tile={<SitesTile />}
      />
    ),
  };
  const slides = defs.map((d) => {
    if (d.kind === 'static') return staticSlides[d.staticKey || 'brand'] || null;
    const product = d.product || null;
    const to = (d.primaryCta?.to || '/course/{productId}').replace('{productId}', d.productId || '');
    const dateShort = product?.eventDate ? new Date(product.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const dateLong = product?.eventDate ? new Date(product.eventDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
    const kicker = dateShort ? `${d.kickerPrefix || 'Live event'} · ${dateShort}` : (d.kickerPrefix || 'Featured event');
    const subhead = (d.subhead || product?.description?.split('\n')[0] || '').replace('{date}', dateLong ? ` · ${dateLong}` : '');
    const top = <span style={{ display: 'block' }}>{d.headlineTop}{d.markerFirst ? '' : ' '}</span>;
    const marker = <span style={MARKER}>{d.headlineMarker}</span>;
    const headline = d.markerFirst ? <><span style={{ display: 'block' }}>{marker}</span>{top}</> : <>{top}{marker}</>;
    const ctas: HeroCTA[] = [{ label: d.primaryCta?.label || 'Reserve your spot', to, primary: true }];
    if (d.secondaryCta?.label) ctas.push(d.secondaryCta.to.startsWith('http') ? { label: d.secondaryCta.label, href: d.secondaryCta.to } : { label: d.secondaryCta.label, to: d.secondaryCta.to });
    return (
      <HeroSlide key={d.id} kicker={kicker} kickerColor={KICKER[d.kickerColor || 'yellow']} headline={headline} subhead={subhead} ctas={ctas}
        tile={<WebinarTile product={product} creator={d.creator || null} to={to} full={d.tileStyle === 'full'} />} />
    );
  }).filter(Boolean) as React.ReactElement[];

  return (
    <section
      onMouseEnter={canHover ? () => setPaused(true) : undefined}
      onMouseLeave={canHover ? () => setPaused(false) : undefined}
      style={{ padding: '40px 0 46px', borderBottom: '1px solid var(--hj-hairline-soft)' }}
    >
      <style>{`
        @media (min-width: 820px) {
          .hj-hero-grid { grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr) !important; }
        }
      `}</style>
      <div className="relative">
        {/* Control row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ marginBottom: 22 }}>
          <div className="flex items-center gap-4">
            <div className="mono-label" style={{ color: 'var(--hj-ink)', fontSize: 17, fontWeight: 800, letterSpacing: '0.09em' }}>What's on Hey Joe Plus</div>
            <Link to="/discover" className="hidden sm:inline-flex items-center gap-1.5" style={{ padding: '9px 16px', borderRadius: 999, background: 'var(--hj-yellow)', border: '1.5px solid var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>Discover <ArrowRight size={13} strokeWidth={2.4} /></Link>
            <Link to="/calendar" className="hidden sm:inline-flex items-center gap-1.5" style={{ padding: '9px 16px', borderRadius: 999, border: '1.5px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>Upcoming Events <ArrowRight size={13} strokeWidth={2.4} /></Link>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <div className="sm:hidden flex items-center gap-2">
              <Link to="/discover" className="inline-flex items-center gap-1.5" style={{ padding: '9px 14px', borderRadius: 999, background: 'var(--hj-yellow)', border: '1.5px solid var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>Discover</Link>
              <Link to="/calendar" className="inline-flex items-center gap-1.5" style={{ padding: '9px 14px', borderRadius: 999, border: '1.5px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>Events</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="mono-label tabular" style={{ color: 'var(--hj-ink)', fontSize: 16, fontWeight: 700 }}>0{index + 1} / 0{slides.length}</span>
              <button type="button" onClick={() => go(index - 1)} aria-label="Previous slide" style={heroNavBtn}><ChevronLeft className="w-6 h-6" /></button>
              <button type="button" onClick={() => go(index + 1)} aria-label="Next slide" style={heroNavBtn}><ChevronRight className="w-6 h-6" /></button>
            </div>
          </div>
        </div>
        {/* Viewport + track */}
        <div style={{ overflow: 'hidden' }}>
          <div ref={trackRef} className="flex" style={{ transition: 'transform .6s cubic-bezier(.6,0,.2,1)' }}>
            {slides.map((s, i) => <div key={i} style={{ flex: '0 0 100%', minWidth: 0 }}>{s}</div>)}
          </div>
        </div>
        {/* Dots */}
        <div className="flex items-center justify-center gap-2" style={{ marginTop: 22 }}>
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`Go to slide ${i + 1}`}
              style={{ width: i === index ? 44 : 15, height: 15, borderRadius: 999, border: 0, padding: 0, cursor: 'pointer', background: i === index ? '#ffd600' : 'color-mix(in oklch, var(--hj-ink) 45%, transparent)', transition: 'width .3s, background .3s' }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatLight: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span
      className="mono-label"
      style={{ color: 'var(--hj-beige-ink-3)' }}
    >
      {label}
    </span>
    <span
      className="truncate inline-flex items-center gap-1.5"
      style={{ fontSize: 13.5, color: 'var(--hj-beige-ink)', fontWeight: 500 }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--hj-green)',
          flexShrink: 0,
        }}
      />
      {value}
    </span>
  </div>
);

// ── Section header helper (eyebrow + title, optional trailing link) ─────
const SectionHead: React.FC<{ eyebrow: string; title: React.ReactNode; link?: { label: React.ReactNode; to?: string } }> = ({ eyebrow, title, link }) => (
  <div className="flex items-end justify-between gap-4 flex-wrap" style={{ marginBottom: 24 }}>
    <div>
      <div className="mono-label" style={{ color: 'var(--hj-yellow-ink)' }}>{eyebrow}</div>
      <h2 className="font-hj-display m-0" style={{ marginTop: 8, fontSize: 'clamp(26px, 3.4vw, 34px)', lineHeight: 1.05, color: 'var(--hj-ink)', fontWeight: 800, letterSpacing: '-0.02em' }}>{title}</h2>
    </div>
    {link && (
      link.to
        ? <Link to={link.to} className="mono-label" style={{ color: 'var(--hj-ink-2)' }}>{link.label}</Link>
        : <span className="mono-label" style={{ color: 'var(--hj-ink-2)' }}>{link.label}</span>
    )}
  </div>
);

// ── Four pillars ───────────────────────────────────────────────────────
const FourPillarsSection: React.FC = () => {
  const navigate = useNavigate();
  return (
    <section style={{ padding: '44px 0 8px' }}>
      <SectionHead eyebrow="The four pillars" title="One membership. Four ways to move your career forward." />
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {PILLARS.map((p) => {
          const clickable = p.href || p.to;
          return (
            <div
              key={p.n}
              className="flex flex-col"
              style={{ background: 'var(--hj-beige)', border: `1px solid ${p.live ? 'color-mix(in oklch, var(--hj-green) 55%, transparent)' : 'var(--hj-beige-line)'}`, borderRadius: 'var(--hj-r-lg)', padding: 20, color: 'var(--hj-beige-ink)' }}
            >
              <div className="font-hj-display" style={{ fontSize: 26, fontWeight: 800, color: 'var(--hj-beige-ink)', lineHeight: 1 }}>{p.n}</div>
              <div className="inline-flex items-center justify-center" style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', margin: '12px 0' }}>
                <p.Icon size={20} strokeWidth={1.9} />
              </div>
              <div className="mono-label" style={{ color: p.accent }}>{p.tag}</div>
              <h3 className="font-hj-display" style={{ marginTop: 4, fontSize: 17, fontWeight: 800, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em', lineHeight: 1.15, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {p.title}
                {p.live && (
                  <span className="mono-label inline-flex items-center gap-1" style={{ padding: '2px 9px 2px 7px', borderRadius: 999, background: 'var(--hj-green)', color: '#fff', fontSize: 9 }}>
                    <span aria-hidden style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />Live
                  </span>
                )}
              </h3>
              <p style={{ margin: '9px 0 0', flexGrow: 1, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hj-beige-ink-2)' }}>{p.blurb}</p>
              {clickable && (
                p.href
                  ? <a href={p.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5" style={{ marginTop: 13, fontSize: 12.5, fontWeight: 800, color: 'var(--hj-beige-ink)' }}>{p.cta} <ArrowRight size={13} strokeWidth={2.2} /></a>
                  : <button type="button" onClick={() => navigate(p.to!)} className="inline-flex items-center gap-1.5" style={{ marginTop: 13, fontSize: 12.5, fontWeight: 800, color: 'var(--hj-beige-ink)', background: 'transparent', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>{p.cta} <ArrowRight size={13} strokeWidth={2.2} /></button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Tools grid ─────────────────────────────────────────────────────────
type ToolFilter = 'all' | 'featured' | 'audio' | 'video';

const ToolsGrid: React.FC = () => {
  const [filter, setFilter] = useState<ToolFilter>('all');
  const visible = TOOLS.filter((t) => {
    if (filter === 'all') return true;
    if (filter === 'featured') return t.hot;
    if (filter === 'audio') return t.id === 'video-transcriber';
    if (filter === 'video') return t.id !== 'video-transcriber';
    return true;
  });
  return (
    <section style={{ padding: '44px 0 8px' }}>
      <div className="flex items-end justify-between gap-4 flex-wrap" style={{ marginBottom: 24 }}>
        <div>
          <div className="mono-label" style={{ color: 'var(--hj-yellow-ink)' }}>Pillar 1 · Enhancer tools</div>
          <h2
            className="font-hj-display m-0"
            style={{
              marginTop: 8,
              fontSize: 'clamp(28px, 3.4vw, 40px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.01em',
            }}
          >
            The studio.
          </h2>
        </div>
        <div className="flex items-center gap-1.5">
          {(['all', 'featured', 'audio', 'video'] as ToolFilter[]).map((f) => (
            <FilterChip
              key={f}
              label={f.charAt(0).toUpperCase() + f.slice(1)}
              active={filter === f}
              onClick={() => setFilter(f)}
            />
          ))}
        </div>
      </div>

      <div
        className="grid gap-3 sm:gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
      >
        {visible.map((t) => (
          <ToolCard key={t.id} tool={t} />
        ))}
      </div>
    </section>
  );
};

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="transition-colors"
    style={{
      padding: '6px 12px',
      borderRadius: 999,
      background: active ? 'var(--hj-surface-3)' : 'transparent',
      border: `1px solid ${active ? 'var(--hj-ink-4)' : 'var(--hj-hairline)'}`,
      color: active ? 'var(--hj-ink)' : 'var(--hj-ink-2)',
      fontSize: 12.5,
      fontWeight: 500,
      fontFamily: 'inherit',
      cursor: 'pointer',
      minHeight: 36,
    }}
  >
    {label}
  </button>
);

const ToolCard: React.FC<{ tool: (typeof TOOLS)[number] }> = ({ tool }) => {
  const { name, blurb, Icon, tag, hot, route, usesCredits } = tool;
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(route)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="text-left flex flex-col gap-4 transition-all"
      style={{
        padding: '22px 22px 20px',
        minHeight: 200,
        background: hover ? 'var(--hj-beige-hover)' : 'var(--hj-beige)',
        border: `1px solid ${hot ? 'var(--hj-pink)' : 'var(--hj-beige-line)'}`,
        borderRadius: 'var(--hj-r-md)',
        color: 'var(--hj-beige-ink)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transform: hover ? 'translateY(-1px)' : 'none',
        transition: 'background 120ms ease, border-color 120ms ease, transform 120ms ease',
      }}
    >
      <div className="flex items-center justify-between">
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 40,
            height: 40,
            borderRadius: 'var(--hj-r-sm)',
            background: hot ? 'var(--hj-pink)' : 'var(--hj-beige-deep)',
            color: 'var(--hj-beige-ink)',
            border: hot ? 'none' : '1px solid var(--hj-beige-line)',
          }}
        >
          <Icon size={18} strokeWidth={1.8} />
        </div>
        {tag && (
          <span
            className="mono-label"
            style={{
              padding: '4px 8px',
              borderRadius: 999,
              background: tag === 'Featured' ? 'var(--hj-yellow)' : 'var(--hj-beige-deep)',
              color: 'var(--hj-beige-ink)',
              fontSize: 9,
              fontWeight: 700,
              border: tag === 'Featured' ? 'none' : '1px solid var(--hj-beige-line)',
              transform: tag === 'Featured' ? 'rotate(-2deg)' : 'none',
            }}
          >
            {tag}
          </span>
        )}
      </div>

      <div className="flex-1">
        <h3
          className="font-hj-display m-0"
          style={{
            fontSize: 24,
            color: 'var(--hj-beige-ink)',
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
          }}
        >
          {name}
        </h3>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 13.5,
            color: 'var(--hj-beige-ink-2)',
            lineHeight: 1.45,
          }}
        >
          {blurb}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5" style={{ fontSize: 13, color: 'var(--hj-beige-ink)', fontWeight: 500 }}>
          Open
          {usesCredits && (
            <span
              aria-hidden
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: 'var(--hj-blue)',
              }}
            />
          )}
        </span>
        <span
          style={{
            color: hot ? 'var(--hj-pink)' : 'var(--hj-beige-ink-3)',
            transform: hover ? 'translateX(2px)' : 'none',
            transition: 'transform 120ms ease',
          }}
        >
          <ArrowRight size={16} strokeWidth={2} />
        </span>
      </div>
    </button>
  );
};

// ── "Book a Reader" callout ────────────────────────────────────────────
const BookReaderSection: React.FC = () => {
  return (
    <section style={{ padding: '44px 0 0' }}>
      <div
        className="grid items-stretch overflow-hidden"
        style={{
          gridTemplateColumns: 'minmax(0, 1fr)',
          minHeight: 200,
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          borderRadius: 'var(--hj-r-lg)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <style>{`
          @media (min-width: 768px) {
            .hj-reader-callout { grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) !important; }
          }
        `}</style>
        <div
          className="hj-reader-callout grid items-center"
          style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}
        >
          {/* Left — copy */}
          <div className="flex flex-col justify-center gap-3" style={{ padding: '32px 34px 30px' }}>
            <div className="mono-label" style={{ color: GOLD_INK }}>
              Pillar 2 · Readers
            </div>
            <h2
              className="font-hj-display m-0"
              style={{
                fontSize: 32,
                lineHeight: 1,
                color: 'var(--hj-beige-ink)',
                letterSpacing: '-0.02em',
              }}
            >
              Book a reader.
            </h2>
            <p
              style={{
                margin: 0,
                maxWidth: 460,
                color: 'var(--hj-beige-ink-2)',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              Practice your audition with a real person. Browse experienced
              readers and coaches ready to help you nail your next role.
            </p>
            <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 6 }}>
              <Link
                to="/readers"
                className="inline-flex items-center gap-1.5"
                style={{
                  padding: '10px 18px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  border: '1px solid var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  textDecoration: 'none',
                  minHeight: 44,
                }}
              >
                Browse readers
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
              <span
                className="inline-flex items-center gap-2"
                style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'var(--hj-green-2, var(--hj-green))',
                  }}
                />
                Sessions from $22
              </span>
            </div>
          </div>

          {/* Right — featured reader cards (real readers + real rates) */}
          <div
            className="flex flex-col justify-center gap-3"
            style={{ padding: '28px 34px', borderTop: '1px solid var(--hj-beige-line)' }}
          >
            <FeaturedReaderCard
              name="Shannon Holmes"
              tagline="Veteran Session Director"
              rate="$25"
              len="30m"
              avatarUrl="https://casting.heyjoe.io/api/s3/upload-2024-04-22T01:06:33.106Z.png"
            />
            <FeaturedReaderCard
              name="Katie Dallas"
              tagline="Working Actor & Reader"
              rate="$20"
              len="30m"
              avatarUrl="https://casting.heyjoe.io/api/s3/upload-2026-01-22T21:19:12.156Z.jpg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturedReaderCard: React.FC<{
  name: string;
  tagline: string;
  rate: string;
  len: string;
  avatarUrl?: string;
}> = ({ name, tagline, rate, len, avatarUrl }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/readers')}
      className="flex items-center gap-3 text-left transition-colors"
      style={{
        width: '100%',
        padding: '14px 16px',
        borderRadius: 'var(--hj-r-md)',
        background: '#fffdf8',
        border: '1px solid var(--hj-beige-line)',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="hj-headshot object-cover flex-shrink-0"
          style={{ width: 44, height: 44 }}
        />
      ) : (
        <ReaderAvatar name={name} size={44} />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate" style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--hj-beige-ink)' }}>
          {name}
        </div>
        <div className="mono-label truncate" style={{ color: 'var(--hj-beige-ink-3)' }}>
          {tagline}
        </div>
      </div>
      <span className="tabular flex-shrink-0" style={{ fontSize: 13, fontWeight: 800, color: 'var(--hj-beige-ink)', whiteSpace: 'nowrap' }}>
        {rate}
        <span style={{ color: 'var(--hj-beige-ink-3)', fontWeight: 500, fontSize: 11 }}>/{len}</span>
      </span>
    </button>
  );
};

// ── Classes & featured events (live products) ─────────────────────────────────
const ClassesWebinarsSection: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CreatorProduct[]>([]);
  useEffect(() => {
    let live = true;
    fetchHomepageHero()
      .then((r) => { if (live) setItems(r.slides.filter((d) => d.kind === 'product' && d.product).map((d) => d.product as CreatorProduct)); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if (items.length === 0) return null;

  const typeLabel = (p: CreatorProduct) => {
    if (p.eventDate) return 'Live workshop';
    if (p.productType === 'series' || p.format === 'series') return 'Course';
    return 'On-demand course';
  };

  return (
    <section style={{ padding: '44px 0 8px' }}>
      <SectionHead
        eyebrow="Pillar 3 · Creator store"
        title="Classes & featured events from working pros."
        link={{ label: 'Browse the marketplace →', to: '/marketplace' }}
      />
      <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
        {items.map((p) => {
          const free = p.price === 0;
          const memberFree = p.memberPriceCents === 0;
          const priceText = free ? 'Free' : `$${(p.price / 100).toFixed(0)}`;
          return (
            <button
              key={p._id}
              type="button"
              onClick={() => navigate(`/course/${p._id}`)}
              className="text-left flex flex-col transition-colors"
              style={{
                minHeight: 140,
                padding: '16px 18px',
                background: 'var(--hj-beige)',
                border: '1px solid var(--hj-beige-line)',
                borderRadius: 'var(--hj-r-md)',
                color: 'var(--hj-beige-ink)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>{typeLabel(p)}</div>
              <h3 className="font-hj-display" style={{ margin: '8px 0 0', fontSize: 16, fontWeight: 800, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em', lineHeight: 1.2 }}>{p.title}</h3>
              <div className="flex items-center gap-2.5 flex-wrap" style={{ marginTop: 'auto', paddingTop: 14 }}>
                {free ? (
                  <span className="mono-label" style={{ padding: '3px 10px', borderRadius: 999, background: 'oklch(0.18 0.005 70)', color: 'var(--hj-yellow-ink)' }}>Free</span>
                ) : memberFree ? (
                  <>
                    <span className="tabular" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink-3)', textDecoration: 'line-through' }}>{priceText}</span>
                    <span className="mono-label" style={{ padding: '3px 10px', borderRadius: 999, background: 'oklch(0.18 0.005 70)', color: 'var(--hj-yellow-ink)' }}>Free for members</span>
                  </>
                ) : (
                  <>
                    <span className="tabular" style={{ fontSize: 15, fontWeight: 800, color: 'var(--hj-beige-ink)' }}>{priceText}</span>
                    {typeof p.memberPriceCents === 'number' && p.memberPriceCents > 0 && p.memberPriceCents < p.price ? (
                      <span className="mono-label" style={{ padding: '3px 10px', borderRadius: 999, background: 'oklch(0.18 0.005 70)', color: 'var(--hj-yellow-ink)' }}>${(p.memberPriceCents / 100).toFixed(0)} for members</span>
                    ) : null}
                    {p.plusOfferMonths ? (
                      <span className="mono-label" style={{ padding: '3px 10px', borderRadius: 999, background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)' }}>+ {p.plusOfferMonths}mo Plus</span>
                    ) : null}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

// ── Community ──────────────────────────────────────────────────────────
const CommunitySection: React.FC = () => (
  <section style={{ padding: '44px 0 8px' }}>
    <div
      className="grid items-center gap-7 overflow-hidden hj-comm"
      style={{ gridTemplateColumns: 'minmax(0,1fr)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', borderRadius: 'var(--hj-r-lg)', padding: 34, color: 'var(--hj-beige-ink)' }}
    >
      <style>{`@media (min-width: 820px){ .hj-comm { grid-template-columns: 1fr 1fr !important; } }`}</style>
      <div>
        <div className="mono-label" style={{ color: GOLD_INK }}>The thread that ties it together</div>
        <h2 className="font-hj-display" style={{ marginTop: 8, fontSize: 26, fontWeight: 800, color: 'var(--hj-beige-ink)', letterSpacing: '-0.015em', lineHeight: 1.2 }}>
          A <span style={{ ...MARKER, fontSize: 'inherit' }}>community</span> around all four pillars.
        </h2>
        <p style={{ margin: '12px 0 0', maxWidth: 460, fontSize: 13.5, lineHeight: 1.6, color: 'var(--hj-beige-ink-2)' }}>
          Hey Joe isn't just tools — it's people. Connect with other performers, learn in the open, and never feel like you're doing this alone.
        </p>
        <button
          type="button"
          onClick={openCommunity}
          className="inline-flex items-center gap-2"
          style={{ marginTop: 18, padding: '12px 22px', borderRadius: 999, background: 'var(--hj-beige-ink)', border: '1px solid var(--hj-beige-ink)', color: 'var(--hj-beige)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 46 }}
        >
          <MessageSquare size={16} strokeWidth={2} />
          Join the Discord
        </button>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div style={{ background: '#fffdf8', border: '1px solid var(--hj-beige-line)', borderRadius: 'var(--hj-r-md)', padding: '17px 18px' }}>
          <div className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', marginBottom: 11 }}>
            <MessageSquare size={18} strokeWidth={1.9} />
          </div>
          <h4 className="font-hj-display" style={{ fontSize: 15, fontWeight: 800, color: 'var(--hj-beige-ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
            Discord channels
            <span className="mono-label" style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--hj-green)', color: '#fff', fontSize: 9 }}>Live</span>
          </h4>
          <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.45, color: 'var(--hj-beige-ink-2)' }}>Rooms for readers, creators, and gigs — feedback, wins, and connections in real time.</p>
        </div>
        <div style={{ background: '#fffdf8', border: '1px solid var(--hj-beige-line)', borderRadius: 'var(--hj-r-md)', padding: '17px 18px' }}>
          <div className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', marginBottom: 11 }}>
            <Users size={18} strokeWidth={1.9} />
          </div>
          <h4 className="font-hj-display" style={{ fontSize: 15, fontWeight: 800, color: 'var(--hj-beige-ink)' }}>Free featured events</h4>
          <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.45, color: 'var(--hj-beige-ink-2)' }}>Learn live from us and other pros — open sessions anyone can join, no membership required.</p>
        </div>
      </div>
    </div>
  </section>
);

// ── Closing CTA band ───────────────────────────────────────────────────
const CTABandSection: React.FC = () => (
  <section style={{ padding: '44px 0 8px' }}>
    <div
      className="flex items-center justify-between gap-6 flex-wrap"
      style={{ padding: 44, borderRadius: 'var(--hj-r-lg)', border: '1px solid color-mix(in oklch, var(--hj-yellow) 40%, transparent)', background: 'linear-gradient(180deg, var(--hj-surface-1), var(--hj-surface-2))' }}
    >
      <div style={{ minWidth: 260 }}>
        <h2 className="font-hj-display m-0" style={{ fontSize: 'clamp(26px, 3.4vw, 34px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.08, color: 'var(--hj-ink)', maxWidth: 620 }}>
          Ready when <span style={MARKER}>you are.</span>
        </h2>
        <p style={{ margin: '12px 0 0', fontSize: 15, color: 'var(--hj-ink-2)', maxWidth: 560, lineHeight: 1.5 }}>
          Join Hey Joe Plus and get the enhancer tools, on-demand readers, your own creator store, the jobs marketplace, and our Discord community — all today.
        </p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/plans" className="inline-flex items-center gap-1.5" style={{ padding: '13px 24px', borderRadius: 999, background: 'var(--hj-yellow)', border: '1px solid var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', textDecoration: 'none', minHeight: 48 }}>
          Join Plus — free <ArrowRight size={15} strokeWidth={2.4} />
        </Link>
        <Link to="/calendar" className="inline-flex items-center gap-1.5" style={{ padding: '13px 22px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 15, fontWeight: 500, fontFamily: 'inherit', textDecoration: 'none', minHeight: 48 }}>
          See a free featured event
        </Link>
      </div>
    </div>
  </section>
);

// ── HomePage container — owns subscription / promo / login flows ───────
// "Have a website?" One strip under the hero: paste the address, see if it can move
// here. The same scan as /scan, with the Move button at the end.
const SiteScanBand: React.FC = () => (
  <section style={{ padding: '8px 0 24px' }}>
    <div style={{ background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)', borderRadius: 20, padding: 'clamp(20px, 3vw, 28px)' }}>
      <div className="flex flex-wrap items-start" style={{ gap: '12px 32px' }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h2 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(22px, 3vw, 28px)', letterSpacing: '-0.02em', lineHeight: 1.1 }}>Have a website? See if it can move here.</h2>
          <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: 1.5, color: 'var(--hj-ink-2)' }}>Hosting is included with Plus. Paste your address and we’ll tell you in a few seconds what your site is built with and whether it can come over as it is.</p>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <SiteScan context="public" compact />
        </div>
      </div>
    </div>
  </section>
);

export const HomePage: React.FC = () => {
  const { isSubscriber, startSubscription, loading, fetchSubscription } =
    useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [promoConfirmation, setPromoConfirmation] = useState<string | null>(null);
  const [showReaderPromo, setShowReaderPromo] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const promoRedeemed = useRef(false);

  const isAuthenticated = !!(
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('castingUser')
  );

  // Auto-redeem promo code from URL (?code=bookit3)
  const redeemPromo = async (code: string) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch('/api/subscription/redeem-promo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ promoCode: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to redeem promo code');
      if (data.checkoutUrl) {
        toast({ title: 'Starting free trial…', description: data.message });
        window.location.href = data.checkoutUrl;
        return;
      }
      if (data.notify) {
        setPromoConfirmation(data.message);
      } else {
        toast({ title: 'Promo code applied!', description: data.message });
      }
      searchParams.delete('code');
      setSearchParams(searchParams, { replace: true });
      fetchSubscription();
    } catch (err: any) {
      const needsSub = err.message?.includes('Subscribe');
      if (needsSub) {
        localStorage.setItem('pending_promo_code', code);
        toast({
          title: 'One quick step',
          description: 'Subscribe to Hey Joe Plus to claim your free reader session.',
        });
        startSubscription('monthly');
      } else {
        toast({
          title: 'Promo code error',
          description: err.message,
          variant: 'destructive',
        });
        searchParams.delete('code');
        setSearchParams(searchParams, { replace: true });
      }
    }
  };

  useEffect(() => {
    const code = searchParams.get('code');
    if (!code || promoRedeemed.current) return;
    if (loading) return;
    promoRedeemed.current = true;

    if (isAuthenticated) {
      if (isSubscriber) {
        toast({
          title: 'Already subscribed',
          description: 'This promo is for new subscribers only.',
        });
        searchParams.delete('code');
        setSearchParams(searchParams, { replace: true });
        return;
      }
      redeemPromo(code);
    } else {
      setShowLoginModal(true);
    }
  }, [searchParams, isAuthenticated, loading, isSubscriber]);

  useEffect(() => {
    if (!isAuthenticated || !isSubscriber) return;
    const pendingCode = localStorage.getItem('pending_promo_code');
    if (!pendingCode) return;
    localStorage.removeItem('pending_promo_code');
    redeemPromo(pendingCode);
  }, [isAuthenticated, isSubscriber]);

  // Featured Reader promo popup for non-subscribers (1-day cooldown).
  // The end-date PROMO_END is past; condition naturally returns. Keeping
  // the effect so a future promo just re-enables.
  useEffect(() => {
    if (loading || isSubscriber) return;
    if (searchParams.get('code')) return;
    const PROMO_END = new Date('2026-04-08T00:00:00');
    if (new Date() >= PROMO_END) return;
    const cooldownKey = 'plus_reader_promo_dismissed';
    const dismissed = localStorage.getItem(cooldownKey);
    if (dismissed) {
      const diffDays = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
      if (diffDays < 1) return;
    }
    const timer = setTimeout(() => setShowReaderPromo(true), 800);
    return () => clearTimeout(timer);
  }, [loading, isSubscriber, searchParams]);

  const dismissReaderPromo = () => {
    setShowReaderPromo(false);
    localStorage.setItem('plus_reader_promo_dismissed', Date.now().toString());
  };

  return (
    <>
      <main
        className="mx-auto"
        style={{
          maxWidth: 1248,
          padding: '0 24px 40px',
          background: 'var(--hj-bg)',
          color: 'var(--hj-ink)',
          minHeight: 'calc(100vh - 68px)',
        }}
      >
        <HomeHero />
        <SiteScanBand />
        <FourPillarsSection />
        <ToolsGrid />
        <HomeDemoSection />
        <BookReaderSection />
        <ClassesWebinarsSection />
        <CommunitySection />
        <CTABandSection />
      </main>

      <Footer />

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        trigger="general"
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => {
          setShowLoginModal(false);
          const code = searchParams.get('code');
          if (code) setTimeout(() => redeemPromo(code), 500);
        }}
      />

      {/* Featured Reader promo popup — preserved behavior */}
      {showReaderPromo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={dismissReaderPromo}
        >
          <div
            className="relative w-full max-w-[420px] text-center"
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: 28,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-blue)',
              color: 'var(--hj-ink)',
              boxShadow: '0 20px 60px oklch(0 0 0 / 0.6)',
            }}
          >
            <button
              type="button"
              onClick={dismissReaderPromo}
              aria-label="Close"
              className="absolute"
              style={{
                top: 10,
                right: 10,
                width: 36,
                height: 36,
                borderRadius: 999,
                background: 'transparent',
                border: 0,
                color: 'var(--hj-ink-3)',
                cursor: 'pointer',
              }}
            >
              <X className="mx-auto" size={18} />
            </button>
            <img
              src="https://casting.heyjoe.io/api/s3/upload-2024-04-22T01:06:33.106Z.png"
              alt="Shannon Holmes"
              className="mx-auto hj-headshot object-cover"
              style={{
                width: 88,
                height: 88,
                border: '3px solid var(--hj-blue)',
              }}
            />
            <div
              className="inline-flex mono-label"
              style={{
                marginTop: 14,
                padding: '3px 10px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 10,
                letterSpacing: '0.1em',
              }}
            >
              Featured Reader
            </div>
            <h2
              className="font-hj-display m-0"
              style={{
                marginTop: 10,
                fontSize: 24,
                color: 'var(--hj-ink)',
                letterSpacing: '-0.01em',
              }}
            >
              Shannon Holmes
            </h2>
            <p
              className="mono-label"
              style={{ marginTop: 4, fontSize: 10 }}
            >
              Veteran Session Director
            </p>
            <p
              style={{
                margin: '14px 0',
                fontSize: 14,
                color: 'var(--hj-ink-2)',
                lineHeight: 1.5,
              }}
            >
              Join Hey Joe Plus today and get a <strong>free reader session</strong>{' '}
              with veteran session director Shannon Holmes — a{' '}
              <strong>$25 value</strong>.
            </p>
            <button
              type="button"
              onClick={() => {
                dismissReaderPromo();
                localStorage.setItem('pending_promo_code', 'freereader');
                startSubscription('monthly');
              }}
              className="inline-flex w-full items-center justify-center gap-2 transition-opacity"
              style={{
                padding: '13px 18px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                marginTop: 4,
                minHeight: 48,
              }}
            >
              <Crown size={14} strokeWidth={2} />
              Join Plus & claim free session
            </button>
            <button
              type="button"
              onClick={dismissReaderPromo}
              className="mt-3"
              style={{
                background: 'transparent',
                border: 0,
                color: 'var(--hj-ink-2)',
                fontSize: 13,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Promo confirmation overlay — preserved behavior */}
      {promoConfirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setPromoConfirmation(null)}
        >
          <div
            className="text-center w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: 32,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink)',
              boxShadow: '0 20px 60px oklch(0 0 0 / 0.6)',
            }}
          >
            <div
              className="mx-auto flex items-center justify-center"
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'oklch(0.42 0.15 145 / 0.18)',
                color: 'var(--hj-green)',
                marginBottom: 16,
              }}
            >
              <CheckCircle size={28} strokeWidth={2} />
            </div>
            <h2 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)' }}>
              You're all set!
            </h2>
            <p style={{ marginTop: 8, color: 'var(--hj-ink-2)', fontSize: 14 }}>
              {promoConfirmation}
            </p>
            <p
              style={{
                marginTop: 6,
                fontSize: 12.5,
                color: 'var(--hj-ink-2)',
              }}
            >
              A confirmation email has been sent to your inbox.
            </p>
            <button
              type="button"
              onClick={() => setPromoConfirmation(null)}
              className="inline-flex items-center"
              style={{
                marginTop: 18,
                padding: '10px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};
